import type { CardsProps } from '@risk-smart/themed-cloudscape-components/cards';
import Cards from '@risk-smart/themed-cloudscape-components/cards';
import Container from '@risk-smart/themed-cloudscape-components/container';
import Header from '@risk-smart/themed-cloudscape-components/header';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { Badge } from '@risksmart-app/atomic-ui';
import Button from '@risksmart-app/components/src/button';
import useLink from '@risksmart-app/components/src/hooks/use-link';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import {
  Obligation_Type_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Permission } from 'src/rbac/Permission';

import Link from '@/components/link';
import { useGetObligationById } from '@/hooks/queries';
import { getFriendlyId } from '@/utils/friendlyId';
import {
  addChildObligationUrl,
  addObligationUrl,
  obligationDetailsUrl,
} from '@/utils/urls';

import type { ObligationTableFields } from '../types';
import styles from './style.module.scss';

type CardItem = {
  id: string;
  title: string;
  rating: { color: string; label: string; value: number } | null;
  SequentialId: null | number | undefined;
  regulatorLabel?: string;
};
interface Props {
  loading: boolean;
  type: Obligation_Type_Enum;
  selectedId?: string;
  onSelectAction: (obligationId: string, type: Obligation_Type_Enum) => void;
  obligations: readonly ObligationTableFields[];
}
interface RatingsByValue {
  [key: number]: { label: string; color: string; value: number };
}

const childTypes: {
  [key in Obligation_Type_Enum]: Obligation_Type_Enum;
} = {
  [Obligation_Type_Enum.Standard]: Obligation_Type_Enum.Chapter,
  [Obligation_Type_Enum.Chapter]: Obligation_Type_Enum.Rule,
  [Obligation_Type_Enum.Rule]: Obligation_Type_Enum.Task,
  [Obligation_Type_Enum.Task]: Obligation_Type_Enum.Task,
};

const unlinkedId = 'unlinked';

const cardItemsFromData = (
  obligations: readonly ObligationTableFields[],
  type: Obligation_Type_Enum,
  title: string,
  ratings: RatingsByValue,
  selectedId: string | undefined
) => {
  const cardItems: CardItem[] = obligations
    .filter(
      (o) =>
        o.Type === type &&
        (o.ParentId === selectedId ||
          o.Type === Obligation_Type_Enum.Standard ||
          (selectedId === unlinkedId && _.isNil(o.ParentId)))
    )
    .map((item) => ({
      id: item.Id,
      title: item.Title,
      SequentialId: item.SequentialId,
      rating: ratings[item.LatestAssessmentResult || 0],
      regulatorLabel: item.RegulatorLabel ?? undefined,
    }));

  if (
    type !== Obligation_Type_Enum.Task &&
    obligations.filter((o) => o.Type == childTypes[type] && _.isNil(o.ParentId))
      .length > 0
  ) {
    cardItems.push({
      title,
      rating: null,
      id: unlinkedId,
      SequentialId: 0,
    });
  }

  cardItems.sort((a, b) => {
    if (a.id === unlinkedId) {
      return 1;
    }
    if (b.id === unlinkedId) {
      return -1;
    }

    return (a.title ?? '').localeCompare(b.title ?? '', undefined, {
      sensitivity: 'base',
    });
  });

  return cardItems;
};

const Type: FC<Props> = ({
  type,
  onSelectAction,
  obligations,
  selectedId,
  loading,
}) => {
  const { handleFollow } = useLink({
    state: {
      from: 'compliance-dashboard',
    },
  });

  const { t } = useTranslation(['common'], {
    keyPrefix: 'obligations',
  });
  const { t: tc } = useTranslation(['common']);
  const { t: tr } = useTranslation(['ratings']);

  const [selectedItems, setSelectedItems] = useState<CardItem[]>([]);
  let empty = '';
  const cardItems = useMemo<CardItem[]>(() => {
    const ratingsByValue: RatingsByValue = [
      ...tr('performance_result_unrated'),
      ...tr('performance_result'),
    ].reduce((sorted, res) => ({ ...sorted, [res.value]: res }), {});

    return cardItemsFromData(
      obligations,
      type,
      t('orphaned_obligation_title'),
      ratingsByValue,
      selectedId
    );
  }, [obligations, selectedId, t, tr, type]);

  empty = cardItems.length === 0 ? tc('noItemsFound') : '';
  const onSelectionChange: CardsProps<CardItem>['onSelectionChange'] = ({
    detail,
  }) => {
    setSelectedItems(detail.selectedItems);
    onSelectAction(detail.selectedItems[0].id, type);
  };

  // `selectedId` is the id of the obligation selected in the PARENT type's
  // column — the same role `parentRiskId` plays in `risk-dashboard/Tier.tsx`.
  // The synthetic "unlinked" card is a bucket, not a node, so it is not a parent.
  const hasRealParent = !!selectedId && selectedId !== unlinkedId;

  // Fetch the selected parent to get `ancestorContributors` for the permission
  // check. With a `parentObject` the gate resolves instance-level
  // (`can_insert_child`), which is what lets a user whose insert capability comes
  // from an ownership grant — rather than a type-level role grant — create a
  // child here (COI-642). Mirrors Tier.tsx.
  const shouldSkipParentFetch =
    type === Obligation_Type_Enum.Standard || !hasRealParent;
  const {
    data: parentObligationData,
    loading: isParentObligationLoading,
    error: parentObligationError,
  } = useGetObligationById({
    queryArgs: { id: selectedId ?? '' },
    shouldSkip: shouldSkipParentFetch,
  });
  // Explicitly null when there is no parent to fetch, so the gate can never be
  // handed a stale parent from a previous selection.
  const parentObligation = shouldSkipParentFetch
    ? null
    : (parentObligationData?.obligation?.[0] ?? null);

  const addHref = hasRealParent
    ? addChildObligationUrl(selectedId, type)
    : addObligationUrl(type);

  return (
    <div className={styles.type} data-testid={`obligation-type-${type}`}>
      <Container fitHeight variant={'stacked'}>
        <SpaceBetween direction={'vertical'} size={'m'}>
          <Header
            variant={'h3'}
            actions={
              // Render nothing while the parent resolves, rather than a
              // denied-looking gap that then pops in (Tier.tsx does the same).
              // Also hide when the parent lookup completed but returned no result
              // (empty data, not loading, not error) — the parent was deleted or
              // is not accessible.
              !shouldSkipParentFetch &&
              (isParentObligationLoading ||
                parentObligationError ||
                !parentObligation) ? null : (
                <Permission
                  permission={'insert:obligation'}
                  canHaveAccessAsContributor={
                    type !== Obligation_Type_Enum.Standard
                  }
                  parentObject={parentObligation}
                >
                  <Button
                    iconName={'add-plus'}
                    variant={'primary'}
                    href={addHref}
                    onFollow={handleFollow}
                  >
                    {tc('create')}
                  </Button>
                </Permission>
              )
            }
          >
            {t('dashboard_category_titles')[type] || ''}
          </Header>
          <Cards<CardItem>
            ariaLabels={{
              // todo: Translation
              itemSelectionLabel: (e, n) => `select ${n.title}`,
              // todo: Translation
              selectionGroupLabel: 'Item selection',
            }}
            entireCardClickable={true}
            cardDefinition={{
              sections: [
                {
                  id: 'title',
                  content: (item) => (
                    <div className={'inline-block'}>
                      <Link
                        variant={'secondary'}
                        href={
                          item.id !== unlinkedId
                            ? obligationDetailsUrl(item.id)
                            : undefined
                        }
                      >
                        <Header
                          variant={'h3'}
                          data-unlinked={item.id === unlinkedId}
                        >
                          <span className={'text-base'}>{item.title}</span>
                        </Header>
                      </Link>
                    </div>
                  ),
                },
              ],
              header: (item) =>
                item.id === unlinkedId ? null : (
                  <div className={'flex'}>
                    {item.id !== unlinkedId && (
                      <div
                        className={
                          'text-grey text-sm flex-grow whitespace-nowrap mr-2'
                        }
                      >
                        {getFriendlyId(
                          Parent_Type_Enum.Obligation,
                          item.SequentialId
                        )}
                      </div>
                    )}
                    {item.regulatorLabel && (
                      <Badge variant={'primary'} className={'mr-2'}>
                        {item.regulatorLabel}
                      </Badge>
                    )}
                    {item.rating !== null && (
                      <SimpleRatingBadge rating={item.rating} />
                    )}
                  </div>
                ),
            }}
            cardsPerRow={[{ cards: 1 }]}
            items={cardItems}
            empty={empty}
            loading={loading}
            loadingText={t('loading_message')}
            visibleSections={['title']}
            selectionType={'single'}
            selectedItems={selectedItems}
            onSelectionChange={onSelectionChange}
            trackBy={(item) => item.id ?? item.title}
          />
        </SpaceBetween>
      </Container>
    </div>
  );
};

export default Type;
